import { useEffect, useRef, useState, type ChangeEvent, type JSX } from 'react';
import { TAILLE_MAX_FICHIER_MO, acceptDeFichiers, libelleDesTypes } from '@taki/data-model';
import type { FichiersDeJeu, StoredFile } from '@taki/data-model';
import type { FileParent, TakiSdk } from '@taki/sdk';
import {
  Button, CardEmptyState, CardHeading, CardSearchField, ConfirmDialog, IconAjouterFichier, IconBulletList, IconDelete, IconDownload, IconTextFile,
  IconVignettes, Input, ViewSelector, VoileDeDepot, peutEcrireSurPorte, useAccesPort, useGlisserDeFichiers,
} from '@taki/ui';
import type { TailleDeVignettes, ViewOption } from '@taki/ui';
import { refusDeFichier } from './fichiers.js';
import { signalerLesPiecesJointes } from './boutons-flux.js';
import { cleDExport, suiviDesExports } from './fiche-en-pdf.js';
import { useEtatDExport } from './gestes-de-fiche.js';
import { VisionneuseDImages } from './VisionneuseDImages.js';
import { imageFixe } from './imageFixe.js';
import {
  AFFICHAGE_D_OUVERTURE, affichageDeVue, colonnesDeVignettes, estAnimable, estUneImage, extensionAffichee, formatAffiche, idDeVue,
  LECTURES_SIMULTANEES, limiteurDeLectures, tailleAffichee,
  type AffichageDesFichiers,
} from './vignettes.js';

const PAGE_SIZE = 50;

/* LES DEUX ENTREES DU SELECTEUR, celui des fiches et des collections (`ViewSelector`),
   icone seule a 15 comme les vues du socle (`views/core/index.ts`). */
const VUES_DAFFICHAGE: ViewOption[] = [
  { id: idDeVue('liste'), label: 'Liste', icon: <IconBulletList size={15} />, level: 'collection', disabled: false },
  { id: idDeVue('vignettes'), label: 'Vignettes', icon: <IconVignettes size={15} />, level: 'collection', disabled: false },
];

/* La boite d'une action posee sur une vignette : un carre, le glyphe seul. */
const ACTION_DE_VIGNETTE = 'h-7 w-7 shrink-0 px-0';
/* La meme, au bout d'une ligne de la liste : le carre de l'ajout de l'en-tete. */
const ACTION_DE_LIGNE = 'h-8 w-8 shrink-0 px-0';

function message(error: unknown): string {
  if (rightsError(error)) return 'Accès aux fichiers retiré. Actualise la fiche après rétablissement des droits.';
  return error instanceof Error ? error.message : 'Une erreur est survenue.';
}

function rightsError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return code === 'permission-denied' || code === 'storage/unauthorized' || code === 'functions/permission-denied';
}

function taille(bytes: number): string {
  return bytes < 1024 ? `${bytes} o` : bytes < 1048576
    ? `${(bytes / 1024).toLocaleString('fr-LU', { maximumFractionDigits: 1 })} Ko`
    : `${(bytes / 1048576).toLocaleString('fr-LU', { maximumFractionDigits: 1 })} Mo`;
}

/**
 * LA CARTE DES FICHIERS JOINTS de la fiche ouverte, a son rang parmi les autres
 * cartes (`{ titre, fichiers: true }`). L'hote ne la monte que si le jeu ouvre
 * les fichiers : `politique` est cette capacite (types, taille), `modifiable`
 * la lecture seule de la carte, toujours bornee par les droits de la porte.
 */
export function FilesPanel({ sdk, collection, type, recordId, titre, politique, modifiable = true, vignettes }: {
  sdk: TakiSdk;
  collection: string;
  type?: string | undefined;
  recordId: string;
  titre: string;
  politique: FichiersDeJeu;
  modifiable?: boolean;
  /**
   * Le reglage de la carte (`carte.vignettes`) : la taille des vignettes ou
   * elle s'ouvre toujours ; absent, les moyennes.
   */
  vignettes?: TailleDeVignettes | undefined;
}): JSX.Element {
  const acces = useAccesPort();
  const canWrite = modifiable && peutEcrireSurPorte(acces, collection, type);
  const parent: FileParent = { collection, id: recordId };
  const inputRef = useRef<HTMLInputElement>(null);
  const requestVersion = useRef(0);
  const [search, setSearch] = useState('');
  const [prefix, setPrefix] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [files, setFiles] = useState<StoredFile[]>([]);
  /*
   * L'AJOUT NE RECHARGE PAS LA CARTE (recette du 02/11 : les vignettes
   * disparaissaient, « Chargement » clignotait, puis tout revenait). Un fichier
   * lache pose TOUT DE SUITE sa vignette d'attente (`envois` : la case vide, son
   * nom, un cercle de chargement), a la fin de la grille ; arrive, il prend sa
   * place (`ajoutes`), sans relire la liste. `files` reste la liste du serveur,
   * dans son ordre : « Voir plus » repart de son dernier element. Rouverte, la
   * fiche range les ajouts a leur place par nom.
   */
  const [envois, setEnvois] = useState<{ cle: string; nom: string }[]>([]);
  const [ajoutes, setAjoutes] = useState<StoredFile[]>([]);
  const tous = [...files, ...ajoutes.filter((ajoute) => !files.some((file) => file.id === ajoute.id))];
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ text: string; retry: boolean } | null>(null);
  const [toRemove, setToRemove] = useState<StoredFile | null>(null);
  const [accessBlocked, setAccessBlocked] = useState(false);

  /*
   * L'AFFICHAGE (liste ou vignettes) est un ETAT DU PANNEAU : la carte s'ouvre
   * en vignettes, son reglage n'en donne que la taille, la bascule ne change
   * que cet etat et ne retient rien. La taille change (Studio, Parametres de
   * la porte) : on repart des vignettes.
   */
  const [affichage, setAffichage] = useState<AffichageDesFichiers>(AFFICHAGE_D_OUVERTURE);
  useEffect(() => { setAffichage(AFFICHAGE_D_OUVERTURE); }, [vignettes]);
  const tailleDesVignettes = tailleAffichee(vignettes);

  /*
   * LES IMAGES DES VIGNETTES. Aucune URL durable : on lit les OCTETS
   * (`sdk.files.download`, les droits de la fiche s'appliquent a chaque lecture,
   * voir l'entete de `photos.ts`) et on en fait une URL d'objet, en cache par id
   * dans ce panneau, revoquee au demontage. Quatre lectures a la fois au plus, et
   * seulement quand les vignettes sont affichees : la liste ne lit rien.
   * Le cache est une REF (pas un etat) : `version` ne sert qu'a redessiner.
   */
  const urls = useRef(new Map<string, string>());
  /*
   * UNE IMAGE ANIMEE (un GIF) NE JOUE PAS DANS LA GRILLE (recette du 02/11 :
   * « c'est un peu perturbant ») : sa vignette montre sa premiere image, figee
   * (`fixes`), et l'animation ne joue qu'au survol ou au focus de la vignette
   * (`animee`), et dans la visionneuse, qui lit `urls`.
   */
  const fixes = useRef(new Map<string, string>());
  const [animee, setAnimee] = useState<string | null>(null);
  const enLecture = useRef(new Set<string>());
  const illisibles = useRef(new Set<string>());
  const limite = useRef(limiteurDeLectures(LECTURES_SIMULTANEES));
  const vivant = useRef(false);
  const [, setVersion] = useState(0);
  useEffect(() => {
    vivant.current = true;
    const lues = urls.current;
    const figees = fixes.current;
    return () => {
      vivant.current = false;
      for (const url of [...lues.values(), ...figees.values()]) URL.revokeObjectURL(url);
      lues.clear();
      figees.clear();
      enLecture.current.clear();
      illisibles.current.clear();
    };
  }, []);
  useEffect(() => {
    if (affichage !== 'vignettes') return;
    for (const file of tous) {
      if (!estUneImage(file.mimeType) || urls.current.has(file.id) || enLecture.current.has(file.id) || illisibles.current.has(file.id)) continue;
      enLecture.current.add(file.id);
      void limite.current(() => sdk.files.download(file.id))
        .then((octets) => {
          if (!vivant.current) return;
          poserImage(file.id, new Blob([octets as BlobPart], { type: file.mimeType }), file.mimeType);
        })
        .catch(() => { if (vivant.current) illisibles.current.add(file.id); })
        .finally(() => {
          if (!vivant.current) return;
          enLecture.current.delete(file.id);
          setVersion((value) => value + 1);
        });
    }
  }, [affichage, files, ajoutes, sdk]);

  /* L'URL d'une image lue, et pour une image animee, sa premiere image figee ; faute
     de pouvoir la figer, la vignette joue l'animation plutot que de ne rien montrer. */
  function poserImage(id: string, octets: Blob, mimeType: string): void {
    const url = URL.createObjectURL(octets);
    urls.current.set(id, url);
    if (!estAnimable(mimeType)) return;
    void imageFixe(url)
      .then((fixe) => { if (vivant.current && urls.current.has(id)) fixes.current.set(id, URL.createObjectURL(fixe)); })
      .catch(() => { if (vivant.current) fixes.current.set(id, url); })
      .finally(() => { if (vivant.current) setVersion((value) => value + 1); });
  }

  /* LA VISIONNEUSE : l'id de l'image ouverte, et les vignettes pour lui rendre le focus. */
  const [ouverte, setOuverte] = useState<string | null>(null);
  const boutonsDeVignette = useRef(new Map<string, HTMLButtonElement>());
  function fermerLaVisionneuse(): void {
    const id = ouverte;
    setOuverte(null);
    if (id !== null) window.setTimeout(() => boutonsDeVignette.current.get(id)?.focus(), 0);
  }

  /*
   * UN PDF DE CETTE FICHE EN COURS (« Exporter en PDF », menu « ... ») : la
   * carte le dit, et se montre, puisque c'est ici que le PDF va apparaitre.
   * Fait, elle se relit ; en echec, elle dit pourquoi. Le suivi est oublie
   * une fois lu, pour qu'une fiche rouverte ne redise pas un vieil avis.
   */
  const sectionRef = useRef<HTMLElement>(null);
  const cleExport = cleDExport(collection, recordId);
  const exportPdf = useEtatDExport(cleExport);
  const [avisPdf, setAvisPdf] = useState<string | null>(null);
  useEffect(() => {
    if (exportPdf === undefined) return;
    if (exportPdf.etat === 'enCours') {
      setAvisPdf(null);
      sectionRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    if (exportPdf.etat === 'fait') {
      setAvisPdf(exportPdf.avertissement ?? `PDF téléchargé et ajouté : ${exportPdf.nom}`);
      setRefresh((value) => value + 1);
    } else {
      setAvisPdf(null);
      setError({ text: `PDF non produit. ${exportPdf.message}`, retry: false });
    }
    suiviDesExports.poser(cleExport, null);
  }, [exportPdf, cleExport]);

  function report(error: unknown, retry: boolean): void {
    if (rightsError(error)) { setAccessBlocked(true); setFiles([]); setHasMore(false); }
    setError({ text: message(error), retry });
  }

  useEffect(() => {
    const timer = window.setTimeout(() => setPrefix(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let live = true;
    requestVersion.current += 1;
    setLoading(true);
    setLoadingMore(false);
    setHasMore(false);
    setError(null);
    setAccessBlocked(false);
    setOuverte(null);
    setFiles([]);
    setAjoutes([]);
    void sdk.files.list({ collection, id: recordId }, { namePrefix: prefix, limit: PAGE_SIZE })
      .then((result) => { if (live) { setFiles(result); setHasMore(result.length === PAGE_SIZE); } })
      .catch((cause: unknown) => { if (live) report(cause, true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [sdk, collection, recordId, prefix, refresh]);

  async function loadMore(): Promise<void> {
    const last = files.at(-1);
    if (!last || !hasMore || loadingMore) return;
    const version = requestVersion.current;
    setLoadingMore(true);
    setError(null);
    try {
      const next = await sdk.files.list(parent, {
        namePrefix: prefix,
        limit: PAGE_SIZE,
        after: { nameKey: last.nameKey, id: last.id },
      });
      if (version !== requestVersion.current) return;
      setFiles((current) => [...current, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (cause) {
      if (version === requestVersion.current) report(cause, true);
    } finally {
      if (version === requestVersion.current) setLoadingMore(false);
    }
  }

  async function attach(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    await joindre([file]);
  }

  /* L'AJOUT, par le bouton comme par le depot : un fichier a la fois, dans
     l'ordre. Un fichier refuse ne retient pas les autres ; le premier refus se
     dit sous l'en-tete, comme avant. */
  async function joindre(fichiers: readonly File[]): Promise<void> {
    const refus = fichiers.map((file) => refusDeFichier(politique, file));
    const acceptes = fichiers.filter((_, i) => refus[i] === null);
    const premierRefus = refus.find((texte) => texte !== null) ?? null;
    setError(premierRefus === null ? null : { text: premierRefus, retry: false });
    if (acceptes.length === 0) return;
    const enAttente = acceptes.map((file, i) => ({ cle: `${Date.now()}:${i}:${file.name}`, nom: file.name }));
    setEnvois((courants) => [...courants, ...enAttente]);
    setBusy('attach');
    try {
      for (const [i, file] of acceptes.entries()) {
        const arrive = await sdk.files.attach({ parent, name: file.name, mimeType: file.type || 'application/octet-stream', bytes: file, kind: 'attachment' });
        /* L'image arrivee se montre par les octets deja en main : aucune seconde lecture. */
        if (estUneImage(arrive.mimeType) && !urls.current.has(arrive.id)) poserImage(arrive.id, file, arrive.mimeType);
        const cle = enAttente[i]?.cle;
        setAjoutes((courants) => [...courants, arrive]);
        setEnvois((courants) => courants.filter((envoi) => envoi.cle !== cle));
      }
    } catch (cause) {
      report(cause, false);
    } finally {
      setEnvois((courants) => courants.filter((envoi) => !enAttente.some((attente) => attente.cle === envoi.cle)));
      setBusy(null);
      /* LES BOUTONS QUI LISENT `socle:piecesJointes` RELISENT LA LISTE. */
      signalerLesPiecesJointes();
    }
  }

  /* LE DEPOT PAR GLISSER, sur toute la fenetre, des que la carte peut recevoir
     un fichier : le voile du socle dit OU il tombe (le titre de la carte) et CE
     QUI passe (la politique du jeu), puis se ferme seul. */
  const glisse = useGlisserDeFichiers(canWrite && !accessBlocked && busy === null, (fichiers) => { void joindre(fichiers); });

  async function download(file: StoredFile): Promise<void> {
    setBusy(file.id);
    setError(null);
    try {
      const bytes = await sdk.files.download(file.id);
      const blob = new Blob([new Uint8Array(bytes)], { type: file.mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (cause) {
      report(cause, false);
    } finally {
      setBusy(null);
    }
  }

  async function remove(): Promise<void> {
    if (!toRemove) return;
    const id = toRemove.id;
    setToRemove(null);
    setBusy(id);
    setError(null);
    try {
      await sdk.files.remove(id);
      /* Retire sur place, sans relire la carte : rien ne clignote. */
      setFiles((courants) => courants.filter((file) => file.id !== id));
      setAjoutes((courants) => courants.filter((file) => file.id !== id));
      for (const cache of [urls.current, fixes.current]) {
        const url = cache.get(id);
        if (url !== undefined) { URL.revokeObjectURL(url); cache.delete(id); }
      }
      signalerLesPiecesJointes();
    } catch (cause) {
      report(cause, false);
    } finally {
      setBusy(null);
    }
  }

  const accept = acceptDeFichiers(politique);
  const images = tous.filter((file) => estUneImage(file.mimeType));

  /* UNE VIGNETTE : une zone carree cliquable (l'image, ou pour un PDF ou un
     document une vignette TYPE : l'icone de document et l'extension), le nom
     tronque dessous. Une image s'AGRANDIT dans la visionneuse ; un document se
     TELECHARGE, comme « Télécharger » de la liste, donc il n'a pas de second
     bouton de telechargement (deux boutons de meme nom, pour le meme geste).
     Les gestes de la vignette (Télécharger d'une image, Supprimer) sont visibles
     au survol et au focus, et toujours sur un ecran tactile. */
  function vignetteDe(file: StoredFile): JSX.Element {
    const image = estUneImage(file.mimeType);
    const animable = image && estAnimable(file.mimeType);
    const url = !image ? undefined : animable && animee !== file.id ? fixes.current.get(file.id) : urls.current.get(file.id);
    const extension = extensionAffichee(file.name);
    const grande = tailleDesVignettes === 'grandes';
    const survol = animable
      ? {
        onMouseEnter: () => setAnimee(file.id),
        onMouseLeave: () => setAnimee((courante) => (courante === file.id ? null : courante)),
        onFocus: () => setAnimee(file.id),
        onBlur: () => setAnimee((courante) => (courante === file.id ? null : courante)),
      }
      : {};
    return <li key={file.id} className="group relative min-w-0" {...survol}>
      <button
        type="button"
        ref={(element) => { if (element === null) boutonsDeVignette.current.delete(file.id); else boutonsDeVignette.current.set(file.id, element); }}
        className="relative flex aspect-square w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-md border border-border bg-surface-1 text-ink-500 transition-colors hover:border-border-strong hover:bg-surface-3 focus-visible:outline-none focus-visible:shadow-ring disabled:cursor-not-allowed disabled:pointer-events-none"
        disabled={accessBlocked || (!image && busy !== null)}
        aria-label={image ? `Agrandir ${file.name}` : `Télécharger ${file.name}`}
        onClick={() => { if (image) setOuverte(file.id); else void download(file); }}
      >
        {url !== undefined
          ? <img src={url} alt="" draggable={false} className="h-full w-full object-cover" />
          : <>
            <IconTextFile size={tailleDesVignettes === 'petites' ? 28 : grande ? 56 : 40} />
            {extension !== '' && <span className="text-[0.625rem] font-semibold uppercase leading-none tracking-wide">{extension}</span>}
          </>}
        {busy === file.id && <span aria-hidden className="absolute inset-0 flex items-center justify-center bg-surface-1"><span className="taki-spinner" /></span>}
      </button>
      <span className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
        {image && <Button variant="secondary" size="sm" className={ACTION_DE_VIGNETTE} disabled={accessBlocked || busy !== null} onClick={() => { void download(file); }} title="Télécharger" aria-label={`Télécharger ${file.name}`}><IconDownload size={14} /></Button>}
        {canWrite && <Button variant="secondary" size="sm" className={ACTION_DE_VIGNETTE} disabled={accessBlocked || busy !== null} onClick={() => setToRemove(file)} title="Supprimer" aria-label={`Supprimer ${file.name}`}><IconDelete size={14} /></Button>}
      </span>
      <p className="mt-1 truncate text-xs text-ink-900" title={file.name}>{file.name}</p>
    </li>;
  }

  /* LE TITRE EST CELUI DES AUTRES CARTES (`CardHeading`), et tout le reste tient
     sur sa ligne, a droite du titre : la RECHERCHE DE BASE du socle
     (`CardSearchField`, la meme que celle des cartes liees), collee au reste a
     droite, aux 3/4 de la recherche d'ecran (16.5 rem au lieu de 22, recette du
     02/11), puis le selecteur de vue (`ViewSelector`), puis
     l'ajout, le dernier, epingle a droite. L'ajout est
     une icone seule (bouton `secondary`, comme les gestes de lien) avec son
     libelle en infobulle. La recherche reste la meme en lecture seule : on
     cherche un fichier pour le telecharger. En lecture seule, ni « Ajouter »
     ni « Supprimer » : on telecharge, c'est tout. */
  return <section ref={sectionRef} className="min-w-0" aria-label={titre}>
    <CardHeading
      title={titre}
      /* UNE CARTE VIDE SE REND EN UNE LIGNE D'ETAT, qui porte son propre blanc :
         meme condition que la branche de l'etat vide plus bas. */
      serre={!loading && !error && tous.length === 0 && envois.length === 0}
      aside={<span className="flex min-w-0 flex-1 items-center gap-2 self-center">
        <CardSearchField className="ml-auto" value={search} onChange={setSearch} />
        <span className="flex shrink-0 items-center gap-2">
          <ViewSelector label="Affichage des fichiers" views={VUES_DAFFICHAGE} activeViewId={idDeVue(affichage)} onViewSelect={(id) => setAffichage(affichageDeVue(id))} />
          {canWrite && <Button variant="secondary" size="sm" className="h-8 w-8 shrink-0 px-0" title="Ajouter un fichier" aria-label="Ajouter un fichier" disabled={accessBlocked || busy !== null} loading={busy === 'attach'} onClick={() => inputRef.current?.click()}><IconAjouterFichier size={16} /></Button>}
        </span>
      </span>}
    />
    {canWrite && <Input ref={inputRef} type="file" className="sr-only" tabIndex={-1} aria-label="Choisir un fichier à joindre" {...(accept === '' ? {} : { accept })} onChange={(event) => { void attach(event); }} />}
    {exportPdf?.etat === 'enCours' && <p role="status" className="mb-3 text-sm text-ink-500">PDF en cours de production...</p>}
    {avisPdf !== null && !error && <p role="status" className="mb-3 text-sm text-ink-500">{avisPdf}</p>}
    {error && <p role="alert" className="mb-3 text-sm text-danger">{error.text} {error.retry && <Button variant="secondary" size="sm" onClick={() => setRefresh((value) => value + 1)}>Réessayer</Button>}</p>}
    {loading ? <p role="status" className="py-3 text-sm text-ink-500">Chargement des fichiers...</p>
      : tous.length === 0 && envois.length === 0 ? !error && <CardEmptyState>{prefix ? 'Aucun résultat' : 'Aucun fichier joint'}</CardEmptyState>
      : affichage === 'vignettes' ? <ul className="grid gap-3" style={{ gridTemplateColumns: colonnesDeVignettes(tailleDesVignettes) }}>
        {tous.map(vignetteDe)}
        {envois.map((envoi) => <li key={envoi.cle} className="min-w-0">
          <span role="status" aria-label={`Envoi de ${envoi.nom}`} className="flex aspect-square w-full items-center justify-center rounded-md border border-border bg-surface-1"><span aria-hidden className="taki-spinner" /></span>
          <p className="mt-1 truncate text-xs text-ink-900" title={envoi.nom}>{envoi.nom}</p>
        </li>)}
      </ul>
      : <ul className="divide-y divide-border">
        {/* UNE LIGNE PAR FICHIER, SUR UNE SEULE LIGNE (recette du 02/11 : « trop
            de blabla ») : le nom en gras, puis le format et la taille, en petit.
            Les gestes sont ceux d'une vignette : deux icones seules, en noir, la
            poubelle comprise (« pas de rouge ») ; le rouge ne vit que dans la
            confirmation de la suppression (G21). */}
        {tous.map((file) => <li key={file.id} className="flex items-center gap-2 py-2">
          <div className="flex min-w-0 flex-1 items-baseline gap-3">
            <p className="truncate text-sm font-medium text-ink-900" title={file.name}>{file.name}</p>
            <p className="shrink-0 text-xs text-ink-500">{formatAffiche(file.mimeType, file.name)} · {taille(file.size)}</p>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button variant="secondary" size="sm" className={ACTION_DE_LIGNE} disabled={accessBlocked || busy !== null} loading={busy === file.id} onClick={() => { void download(file); }} title="Télécharger" aria-label={`Télécharger ${file.name}`}><IconDownload size={16} /></Button>
            {canWrite && <Button variant="secondary" size="sm" className={ACTION_DE_LIGNE} disabled={accessBlocked || busy !== null} onClick={() => setToRemove(file)} title="Supprimer" aria-label={`Supprimer ${file.name}`}><IconDelete size={16} /></Button>}
          </div>
        </li>)}
        {envois.map((envoi) => <li key={envoi.cle} className="flex items-center gap-2 py-2">
          <div className="flex min-w-0 flex-1 items-baseline gap-3">
            <p className="truncate text-sm font-medium text-ink-900" title={envoi.nom}>{envoi.nom}</p>
            <p className="shrink-0 text-xs text-ink-500">Envoi…</p>
          </div>
          <span role="status" aria-label={`Envoi de ${envoi.nom}`} className="flex h-8 w-8 shrink-0 items-center justify-center"><span aria-hidden className="taki-spinner" /></span>
        </li>)}
      </ul>}
    {hasMore && !loading && <div className="pt-3"><Button variant="secondary" size="sm" loading={loadingMore} disabled={busy !== null} onClick={() => { void loadMore(); }}>Voir plus</Button></div>}
    {ouverte !== null && <VisionneuseDImages
      images={images}
      courante={ouverte}
      urlDe={(id) => urls.current.get(id)}
      illisible={(id) => illisibles.current.has(id)}
      occupe={accessBlocked || busy !== null}
      message={error?.text ?? null}
      onNaviguer={setOuverte}
      onTelecharger={(id) => { const cible = tous.find((file) => file.id === id); if (cible) void download(cible); }}
      onFermer={fermerLaVisionneuse}
    />}
    <VoileDeDepot ouvert={glisse} titre={`Déposer dans ${titre}`} precision={`${libelleDesTypes(politique)} · ${politique.tailleMaxMo ?? TAILLE_MAX_FICHIER_MO} Mo max`} />
    <ConfirmDialog open={toRemove !== null} tone="danger" title="Supprimer ce fichier ?" body={`Le fichier « ${toRemove?.name ?? ''} » sera supprimé.`} confirmLabel="Supprimer" onCancel={() => setToRemove(null)} onConfirm={() => { void remove(); }} />
  </section>;
}
